/**
 * Best-effort persistence of the last-seen changelog version.
 *
 * Thin wrapper around `localStorage` for recording which changelog
 * Latest_Version the user has already seen. Every access is best-effort: reads
 * and writes are wrapped in try/catch and never surface an error to the caller
 * (Req 7.2, 7.3, 7.4). This keeps the changelog fully usable even when storage
 * is disabled, blocked, or over quota, and safe under SSR/test environments
 * where `localStorage` may be undefined.
 */

/** Storage key under which the Last_Seen_Version is persisted. */
export const STORAGE_KEY = 'coveredcalls.changelog.lastSeenVersion';

/**
 * Read the persisted Last_Seen_Version.
 *
 * Best-effort and total: returns the stored version string, or `null` when the
 * value is unset, when `localStorage` is unavailable (e.g. SSR/tests), or when
 * access throws (e.g. storage disabled). Never throws (Req 7.2, 7.4).
 */
export function readLastSeenVersion(): string | null {
  try {
    if (typeof localStorage === 'undefined') {
      return null;
    }
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Persist the Last_Seen_Version.
 *
 * Best-effort and total: silently swallows any error (storage disabled, quota
 * exceeded) and no-ops when `localStorage` is unavailable (e.g. SSR/tests), so
 * nothing is surfaced to the caller. Never throws (Req 7.3, 7.4).
 */
export function writeLastSeenVersion(version: string): void {
  try {
    if (typeof localStorage === 'undefined') {
      return;
    }
    localStorage.setItem(STORAGE_KEY, version);
  } catch {
    // Best-effort: ignore write failures (disabled storage / quota exceeded).
  }
}
